/**
 * ComponentCard — one registry component, Bootstrap-Docs style.
 *
 * A LIVE preview (the actual kit component, rendered with a small sample so
 * the card shows the thing rather than a screenshot of it), then the contract:
 * layer · family · what it lays out · variants · states · signals · slots ·
 * sealed · allowed overrides · introduced-in-app · render badge.
 *
 * The sealed/overrides split is the point of the whole registry, so it gets
 * the most visual weight: what a widget author may NOT change, and what they
 * may. `render: "spec"` cards never fake a preview — they draw the ports /
 * recipe mini-graph instead (see ComponentPreview → RecipeGraph).
 */
import ComponentPreview from './ComponentPreview';
import { composedTypes } from './previewSamples';
import { bootstrapAnchorId } from './studioNav';

const C = { text: '#1c1c1e', dim: '#6e6e73', border: '#e2e2ea', card: '#fff', accent: '#6E59F2' };

const LAYER_COLOR = {
  L0: '#8E8E93', L1: '#5856d6', L2: '#0E7C7B', L3: '#B45309', L4: '#B5835A',
};

/**
 * The three answers to "what renders this?" — native kit (green), a DATA
 * composition the runtime expands (violet, and hot-updatable), or a documented
 * contract with no distinct impl yet (gray).
 */
const RENDER_BADGE = {
  implemented: { bg: '#e8f6ec', fg: '#248A3D', line: '#c7e7d1' },
  composed: { bg: '#f1eefe', fg: '#5B45D6', line: '#ddd6f8' },
};

const capLabel = {
  fontSize: 9.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.dim,
};

/**
 * What this component LAYS OUT, as jumps to those components' own cards.
 *
 * A grid is a grid OF something, and the thing it lays out has a card of its
 * own — naming it here is the difference between "ItemCollection · collections"
 * and "ItemCollection lays out Card, go and look". Same accent
 * underline as the focus explorer's type badge, because it is the same gesture:
 * a type you can click to go read it. Composes nothing ⇒ renders nothing.
 */
function Composes({ types, onJump }) {
  if (!types.length || !onJump) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={capLabel}>Lays out</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 11.5 }}>
        {types.map((t) => (
          <button
            key={t} type="button" onClick={() => onJump(t)}
            title={`Show ${t} in the Component Bootstrap`}
            style={{
              font: 'inherit', color: C.accent, background: 'none', border: 'none',
              padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2,
            }}
          >{t} →</button>
        ))}
      </div>
    </div>
  );
}

function Chips({ label, items, mono, tone }) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={capLabel}>
        {label}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {list.map((it, i) => {
          const text = typeof it === 'string' ? it : `${it.name}${it.payload ? ` ${it.payload}` : ''}`;
          return (
            <span key={`${text}-${i}`} style={{
              fontSize: 10.5, padding: '2px 7px', borderRadius: 5,
              background: tone === 'sealed' ? '#fdf3e3' : '#f4f4f7',
              color: tone === 'sealed' ? '#B45309' : C.text,
              border: `1px solid ${tone === 'sealed' ? '#f0dfc0' : C.border}`,
              fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit',
            }}>{text}</span>
          );
        })}
      </div>
    </div>
  );
}

export default function ComponentCard({ comp, onJump = null, highlight = false }) {
  const composes = composedTypes(comp.id);
  return (
    <article
      id={bootstrapAnchorId(comp.id)}
      style={{
        background: C.card,
        border: `2px solid ${highlight ? C.accent : C.border}`,
        borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column',
        gap: 12, minWidth: 0, scrollMarginTop: 96,
        boxShadow: highlight ? `0 0 0 4px ${C.accent}22` : 'none',
        transition: 'border-color .2s, box-shadow .2s',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <code style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{comp.id}</code>
            <span style={{
              fontSize: 9.5, fontWeight: 800, padding: '1px 6px', borderRadius: 4,
              background: `${LAYER_COLOR[comp.layer] || C.dim}1f`, color: LAYER_COLOR[comp.layer] || C.dim,
            }}>{comp.layer}</span>
            <span style={{ fontSize: 10.5, color: C.dim }}>{comp.family}</span>
          </div>
          <div style={{ fontSize: 12, color: C.dim, marginTop: 2 }}>{comp.name}</div>
        </div>
        <span style={{
          fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap',
          background: RENDER_BADGE[comp.render]?.bg || '#f4f4f7',
          color: RENDER_BADGE[comp.render]?.fg || C.dim,
          border: `1px solid ${RENDER_BADGE[comp.render]?.line || C.border}`,
        }}>{comp.render}</span>
      </header>

      <ComponentPreview comp={comp} />

      <p style={{ margin: 0, fontSize: 12, color: C.text, lineHeight: 1.5 }}>{comp.description}</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Composes types={composes} onJump={onJump} />
        <Chips label="Variants" items={comp.variants} mono />
        <Chips label="States" items={comp.states} mono />
        <Chips label="Signals ▸ out" items={comp.signals} mono />
        <Chips label="Slots ◂ in" items={comp.slots} mono />
        <Chips label="Sealed — a widget cannot change these" items={comp.sealed} tone="sealed" />
        <Chips label="Allowed overrides" items={comp.overrides} />
      </div>

      <footer style={{ fontSize: 10.5, color: C.dim, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
        introduced in app {comp.introduced_in_app}
      </footer>
    </article>
  );
}
